import React from 'react';
import {
    Box,
    TextField,
    Typography,
    CircularProgress,
    InputAdornment
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Selection, Sort } from '@syncfusion/ej2-react-grids';
import { ProcedureType } from '../../../../../../../services/ConfigurationService/types/procedureType';

interface ProcedureSelectionGridProps {
    searchTerm: string;
    onSearchTermChange: (term: string) => void;
    filteredProcedures: ProcedureType[];
    isLoading: boolean;
    error?: string;
    onRowSelection: (args: any) => void;
}

export const ProcedureSelectionGrid: React.FC<ProcedureSelectionGridProps> = ({
    searchTerm,
    onSearchTermChange,
    filteredProcedures,
    isLoading,
    error,
    onRowSelection
}) => {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Select a Procedure Code:
            </Typography>

            {/* Search Field */}
            <TextField
                fullWidth
                placeholder="Search procedure codes..."
                value={searchTerm}
                onChange={(e) => onSearchTermChange(e.target.value)}
                size="small"
                sx={{ mb: 2 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#64748b' }} />
                        </InputAdornment>
                    ),
                }}
            />

            {filteredProcedures.length > 0 && (
                <Typography variant="body2" sx={{ mb: 1, color: '#64748b', fontStyle: 'italic', fontSize: '0.75rem' }}>
                    Click on any row to select a procedure code.
                </Typography>
            )}

            {/* Procedure Grid */}
            <Box sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 1,
                bgcolor: '#fff'
            }}>
                {isLoading ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Loading procedure codes...
                        </Typography>
                    </Box>
                ) : filteredProcedures.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            {searchTerm ? 'No procedure codes match your search.' : 'No procedure codes available for this center.'}
                        </Typography>
                    </Box>
                ) : (
                    <GridComponent
                        dataSource={filteredProcedures}
                        height={300}
                        width="100%"
                        rowSelected={onRowSelection}
                        selectionSettings={{ type: 'Single', mode: 'Row' }}
                        allowSorting={true}
                        sortSettings={{ columns: [{ field: 'code', direction: 'Ascending' }] }}
                        style={{ border: 'none' }}
                    >
                        <ColumnsDirective>
                            <ColumnDirective
                                field="code"
                                headerText="Code"
                                width="120"
                                textAlign="Left"
                                allowSorting={true}
                            />
                            <ColumnDirective
                                field="description"
                                headerText="Description"
                                width="300"
                                textAlign="Left"
                                allowSorting={true}
                            />
                            <ColumnDirective
                                field="codeType"
                                headerText="Type"
                                width="80"
                                textAlign="Center"
                                allowSorting={true}
                            />
                        </ColumnsDirective>
                        <Inject services={[Selection, Sort]} />
                    </GridComponent>
                )}
            </Box>

            {error && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
};
